import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { ListSelector } from '~/client/src/shared/components/ListSelector/ListSelector'
import LocationResourcePicker from '~/client/src/shared/components/LocationPicker/LocationResourcePicker'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import Location from '~/client/src/shared/components/SitemapAttributeSelector/Location'
import Level from '~/client/src/shared/models/LocationObjects/Level'
import OffloadingEquipment from '~/client/src/shared/models/LocationObjects/OffloadingEquipment'
import VerticalObject from '~/client/src/shared/models/LocationObjects/VerticalObject'
import BuildingsStore from '~/client/src/shared/stores/domain/Buildings.store'
import LevelsStore from '~/client/src/shared/stores/domain/Levels.store'
import { sortLevels } from '~/client/src/shared/utils/sortingFunctions'
import { getBandTitleByTagType } from '~/client/src/shared/utils/TagHelper'

const accessibleLevelsProperties = {
  properties: 'Properties',
  accessibleLevels: 'Accessible levels',
  editAccessibleLevels: 'Edit accessible levels',
  selectAccessibleLevels: 'Select accessible levels',
}

interface IProps {
  dataObject: VerticalObject | OffloadingEquipment

  className?: string
  onChange?: () => void

  levelsStore?: LevelsStore
  buildingsStore?: BuildingsStore
}

@inject('levelsStore', 'buildingsStore')
@observer
export default class ObjectAccessibleLevelsProperties extends React.Component<IProps> {
  @observable private isLevelsSelectorDisplayed: boolean = false
  @observable private localProps: IProps = null
  private readonly anchorRef = React.createRef<HTMLDivElement>()

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (this.localProps.dataObject !== this.props.dataObject) {
        this.localProps.dataObject = this.props.dataObject
      }
      if (this.localProps.onChange !== this.props.onChange) {
        this.localProps.onChange = this.props.onChange
      }
      if (this.localProps.className !== this.props.className) {
        this.localProps.className = this.props.className
      }
    })
  }

  public render() {
    if (!this.allowedLevels.length || !this.buildingParent) {
      return null
    }

    const { className, dataObject } = this.localProps
    const { type } = dataObject

    return (
      <div
        className={classList({
          'object-accessible-levels-properties properties-section pa12': true,
          [className]: !!className,
        })}
      >
        <div className="text title uppercase pb12">
          {`${getBandTitleByTagType(type)} ${
            accessibleLevelsProperties.properties
          }`}
        </div>
        <div className="relative" ref={this.anchorRef}>
          <div className="text pb4">
            {accessibleLevelsProperties.accessibleLevels}
          </div>
          {this.accessibleLevels.map(l => (
            <div className="y-center row py6" key={l.id}>
              <div>
                <Location dto={l} />
              </div>
              <div className="no-grow">
                <div
                  className="cross-icon"
                  onClick={this.removeLevel.bind(this, l)}
                >
                  ✖
                </div>
              </div>
            </div>
          ))}
          <div
            className="pt12 text blue-highlight bold large pointer"
            onClick={this.openLevelsSelector}
          >
            {accessibleLevelsProperties.editAccessibleLevels}
          </div>
        </div>
        <ResponsiveSurface
          open={this.isLevelsSelectorDisplayed}
          onOpenChange={open => !open && this.closeLevelsSelector()}
          title={accessibleLevelsProperties.selectAccessibleLevels}
          anchor={this.anchorRef.current}
          sizing="fill"
        >
          <ListSelector.Title
            title={accessibleLevelsProperties.selectAccessibleLevels}
            onClose={this.closeLevelsSelector}
          />
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
            <LocationResourcePicker
              mode="multi"
              allowEmptySelection={true}
              selectedIds={this.accessibleLevels.map(l => l.id)}
              allowedIds={this.allowedLevels
                .filter(l => l.isParent(this.buildingParent))
                .map(l => l.id)}
              onSelect={this.onAccessibleLevelsChange}
              onClose={this.closeLevelsSelector}
            />
          </div>
        </ResponsiveSurface>
      </div>
    )
  }

  @computed
  private get allowedLevels(): Level[] {
    const { dataObject, levelsStore } = this.localProps
    if (!dataObject.hasParent) {
      return []
    }
    return levelsStore.list.filter(l => l.hasCommonParent(dataObject))
  }

  @computed
  private get buildingParent() {
    const { dataObject, buildingsStore } = this.localProps
    const { parent } = dataObject
    return parent && buildingsStore.byId.get(parent.parentId)
  }

  private get accessibleLevels() {
    const { accessibleLevels } = this.localProps.dataObject

    const levels = accessibleLevels
      .map(id => this.allowedLevels.find(l => l.id === id))
      .filter(l => l)

    return (
      this.buildingParent?.sortLevelsByCustomOrder(levels) ||
      levels.sort((a, b) => sortLevels(b, a))
    )
  }

  @action.bound
  private openLevelsSelector() {
    this.isLevelsSelectorDisplayed = true
  }

  @action.bound
  private closeLevelsSelector() {
    this.isLevelsSelectorDisplayed = false
  }

  private onAccessibleLevelsChange = (newLevels: string[]) => {
    const { dataObject, onChange } = this.localProps

    dataObject.accessibleLevels = newLevels
    this.closeLevelsSelector()

    onChange?.()
  }

  private removeLevel(level: Level) {
    const { dataObject, onChange } = this.localProps

    dataObject.accessibleLevels = this.accessibleLevels
      .map(l => l.id)
      .filter(id => id !== level.id)

    onChange?.()
  }
}
